import React, {
	useMemo,
	useCallback,
	useState,
	useEffect,
	type ReactNode,
	type ChangeEvent,
	type KeyboardEvent,
} from 'react';
import { styled } from '@compiled/react';
import debounce from 'lodash/debounce';
import SearchIcon from '@atlaskit/icon/glyph/search';
import TextField from '@atlaskit/textfield';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import { useIntl } from '@atlassian/jira-intl';
import {
	MAX_VIEWPORT_ITEMS_IN_SEARCH,
	DEBOUNCE_DELAY,
	CONTEXT_MENU_SEARCH_CONTENT_TESTID,
	CONTEXT_MENU_SEARCH_LOADING_TESTID,
	CONTEXT_MENU_SEARCH_NO_RESULTS_TESTID,
	CONTEXT_MENU_SEARCH_TESTID,
	ITEM_LINE_HEIGHT,
} from '../../../../../common/constants';
import messages from './messages';
import ResultsAnnouncer from './results-announcer';

type BaseProps = {
	onChange?: (value: string) => void;
	autoFocus?: boolean;
	maxViewportItems?: number;
	hasSearch?: boolean;
	hasResult?: boolean;
	children: ReactNode;
	ariaLabel?: string;
	resultsCount: number;
};

type SyncSearchProps = BaseProps & {
	/**
	 * Indicates that the search results are fetched via
	 * an external async request. When true, isLoading is
	 * required
	 * */
	isAsyncSearch: false;
	isLoading?: boolean;
};

type AsyncSearchProps = BaseProps & {
	/**
	 * Indicates that the search results are fetched via
	 * an external async request. When true, isLoading is
	 * required
	 * */
	isAsyncSearch: true;
	isLoading: boolean;
};

export type Props = SyncSearchProps | AsyncSearchProps;

export const ContextMenuSearch = ({
	onChange,
	autoFocus = true,
	hasSearch = true,
	maxViewportItems = MAX_VIEWPORT_ITEMS_IN_SEARCH,
	isLoading = false,
	hasResult = false,
	isAsyncSearch,
	children,
	ariaLabel,
	resultsCount,
}: Props) => {
	const { formatMessage } = useIntl();

	const [query, setQuery] = useState('');

	useEffect(
		() => () => {
			onChange?.(''); // reset the filter result when the component unmounts
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[],
	);

	const debouncedOnChange = useMemo(
		() =>
			debounce((value: string) => {
				onChange?.(value);
			}, DEBOUNCE_DELAY),
		[onChange],
	);

	const handleChange = useCallback(
		(e: ChangeEvent<HTMLInputElement>) => {
			const { value } = e.target;
			debouncedOnChange(value);
			setQuery(value);
		},
		[debouncedOnChange, setQuery],
	);

	const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
		if (e.key === 'Escape' && query !== '') {
			e.preventDefault();
			e.stopPropagation();
			onChange?.('');
			setQuery('');
		}
	};

	return (
		<ContextMenuSearchWrapper data-testid={CONTEXT_MENU_SEARCH_TESTID}>
			{hasSearch && (
				<SearchWrapper role="search">
					<TextField
						role="searchbox"
						aria-label={ariaLabel}
						aria-autocomplete="list"
						elemBeforeInput={
							<IconWrapper>
								<SearchIcon label={formatMessage(messages.search)} size="small" />
							</IconWrapper>
						}
						onChange={handleChange}
						onKeyDown={handleKeyDown}
						placeholder={formatMessage(messages.search)}
						isCompact
						value={query}
						autoFocus={autoFocus}
					/>
					<ResultsAnnouncer
						resultsCount={resultsCount}
						query={query}
						isAsyncSearch={isAsyncSearch}
						isLoading={isLoading}
					/>
				</SearchWrapper>
			)}
			<ContextMenuSearchContent
				maxViewportItems={maxViewportItems}
				data-testid={CONTEXT_MENU_SEARCH_CONTENT_TESTID}
			>
				{isLoading && (
					<ContextMenuSearchMessage data-testid={CONTEXT_MENU_SEARCH_LOADING_TESTID}>
						{formatMessage(messages.loading)}
					</ContextMenuSearchMessage>
				)}
				{!isLoading && !hasResult && (
					<ContextMenuSearchMessage data-testid={CONTEXT_MENU_SEARCH_NO_RESULTS_TESTID}>
						{formatMessage(messages.noResults)}
					</ContextMenuSearchMessage>
				)}
				{!isLoading && hasResult && children}
			</ContextMenuSearchContent>
		</ContextMenuSearchWrapper>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const IconWrapper = styled.div({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	color: `${token('color.icon', colors.N300)}`,
	marginLeft: token('space.100', '8px'),
	display: 'flex',
});

// eslint-disable-next-line @atlaskit/design-system/no-empty-styled-expression, @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ContextMenuSearchWrapper = styled.div({});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const SearchWrapper = styled.form({
	padding: `${token('space.075', '6px')} ${token('space.100', '8px')}`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	height: `${ITEM_LINE_HEIGHT}px`,
	display: 'flex',
	alignItems: 'center',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	borderBottom: `1px solid ${token('color.border', colors.N40)}`,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ContextMenuSearchContent = styled.div<{ maxViewportItems: number }>({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles, @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	maxHeight: ({ maxViewportItems }) => `${ITEM_LINE_HEIGHT * maxViewportItems}px`,
	overflowY: 'auto',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ContextMenuSearchMessage = styled.div({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	height: `${ITEM_LINE_HEIGHT}px`,
	display: 'flex',
	justifyContent: 'center',
	alignItems: 'center',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	color: `${token('color.text.subtle', colors.N200)}`,
});
